import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import {
  NoiseCalculationRow,
  NoiseCalculationInputsContinuo,
  NoiseCalculationInputsImpulsivo,
  NoiseCalculationResultadoContinuo,
  NoiseCalculationResultadoImpulsivo,
} from './gestion-tecnica-ruido'
import { BANDAS_OCTAVA, LIMITE_AM196_8H, LIMITE_PICO_DIR_2003_10, verificarLimitePico, textoAccionPico } from './ruido'

// Genera el reporte técnico PDF de un cálculo de Ruido (continuo o
// impulsivo), siguiendo el mismo patrón que generateLightingReportPDF():
// createPdfHelpers() para el flujo de texto + uploadAndRegisterPdf() para
// subir el blob y registrarlo en sso_documents. A diferencia de Iluminación,
// no dibuja una vista gráfica (no hay equivalente a la planta 2D aquí).
export async function generateNoiseReportPDF(
  calculo: NoiseCalculationRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, hline, skip, doc } = helpers

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Cálculo de Ruido Ocupacional', 12, 'bold', 'center', 0, 6)
  txt(calculo.descripcion, 11, 'bold', 'center', 0, 6)
  hline()

  if (calculo.tipo_ruido === 'continuo') {
    const inputs = calculo.inputs as NoiseCalculationInputsContinuo
    const resultado = calculo.resultado as NoiseCalculationResultadoContinuo

    // b. Datos de entrada
    txt('Datos de entrada — ruido continuo', 11, 'bold', 'left', 0, 4)
    txt(`Fuente metodológica: NTP 638 (INSHT, 2003)`, 9, 'italic', 'left', 0, 3)
    txt(`Horas de exposición al nivel medido: ${inputs.horasExposicion} h (jornada de referencia 8h)`, 9, 'normal', 'left', 0, 2)
    txt(`Minutos que se quita el protector por hora: ${inputs.minutosSinProtectorPorHora} min`, 9, 'normal', 'left', 0, 2)
    txt(`Eficacia de protección: ${inputs.eficacia}%`, 9, 'normal', 'left', 0, 2)
    txt(
      `Lecturas por banda de octava (${BANDAS_OCTAVA.join('/')} Hz)${inputs.lecturas.length > 1 ? `, promedio de ${inputs.lecturas.length} lecturas` : ''}:`,
      9, 'normal', 'left', 0, 2
    )
    inputs.lecturas.forEach((l, i) => {
      txt(`  Lectura ${i + 1}: ${l.join(', ')} dB`, 8, 'normal', 'left', 4, 2)
    })
    txt(`Protector — H: ${inputs.H}  M: ${inputs.M}  L: ${inputs.L}  SNR: ${inputs.SNR}`, 9, 'normal', 'left', 0, 2)
    txt(`Atenuación media por banda (mf): ${inputs.mfPorBanda.join(', ')} dB`, 8, 'normal', 'left', 0, 2)
    txt(`Desviación estándar por banda (σ): ${inputs.sigmaPorBanda.join(', ')} dB`, 8, 'normal', 'left', 0, 4)
    hline()

    // c. Resultado
    txt('Resultado del cálculo', 11, 'bold', 'left', 0, 4)
    txt(`Nivel total medido (LA, sin protector): ${resultado.LA} dBA`, 9, 'normal', 'left', 0, 2)
    txt(`Nivel C global (LC): ${resultado.LC} dBC`, 9, 'normal', 'left', 0, 2)
    txt(`Nivel efectivo con protector — bandas de octava (más preciso): ${resultado.LAp} dBA`, 9, 'bold', 'left', 0, 2)
    txt(`Nivel efectivo con protector — método HML (referencia): ${resultado.LAp_HML} dBA`, 9, 'normal', 'left', 0, 2)
    txt(`Nivel efectivo con protector — método SNR (referencia): ${resultado.LAp_SNR} dBA`, 9, 'normal', 'left', 0, 4)
    txt(`LAeq diario: ${resultado.laeqDiario} dB(A)`, 9, 'normal', 'left', 0, 2)
    txt(
      `Cumplimiento (límite ${LIMITE_AM196_8H} dB(A)/8h): ${resultado.cumpleAM196 ? 'CUMPLE' : 'NO CUMPLE'}`,
      9, 'bold', 'left', 0, 2
    )
    txt(
      `Nivel efectivo real con quitado de protector (${inputs.minutosSinProtectorPorHora} min/h): ${resultado.laeqEfectivoReal} dB(A) — ${resultado.cumpleAM196Real ? 'CUMPLE' : 'NO CUMPLE'}`,
      9, 'normal', 'left', 0, 4
    )
    hline()

    // d. Cita normativa — la misma que se muestra en pantalla
    // (RuidoMethodologyPage.tsx), para que el PDF
    // documente exactamente lo mismo que ve el usuario en vivo, no una
    // versión distinta o más autoritativa.
    txt(
      `Límite de ${LIMITE_AM196_8H} dB(A)/8h — Anexo 3 del AM 196 (2024), Art. 4, numeral 5 "Valores límite de exposición", por remisión del DE 255 Art. 48. ` +
      'Los métodos HML y SNR se muestran ' +
      'como referencia comparativa, no como alternativa al método de bandas de octava (el más preciso, usado para el ' +
      'veredicto de cumplimiento).',
      8, 'italic', 'left', 0, 4
    )
  } else {
    const inputs = calculo.inputs as NoiseCalculationInputsImpulsivo
    const resultado = calculo.resultado as NoiseCalculationResultadoImpulsivo

    // b. Datos de entrada
    txt('Datos de entrada — ruido impulsivo', 11, 'bold', 'left', 0, 4)
    txt('Fuente metodológica: Guía Técnica INSST 2022, Apéndice 4 §4.2', 9, 'italic', 'left', 0, 3)
    txt(`Nivel de pico medido (LCpico): ${inputs.LCpico} dB(C)`, 9, 'normal', 'left', 0, 2)
    txt(`Frecuencia dominante del impulso: ${inputs.frecuenciaDominante}`, 9, 'normal', 'left', 0, 2)
    txt(`Protector — H: ${inputs.H}  M: ${inputs.M}  L: ${inputs.L}`, 9, 'normal', 'left', 0, 4)
    hline()

    // c. Resultado
    txt('Resultado del cálculo', 11, 'bold', 'left', 0, 4)
    txt(`Nivel de pico atenuado (LCpico'): ${resultado.LCpicoAtenuado} dB(C)`, 9, 'bold', 'left', 0, 2)
    txt(
      `Cumplimiento (límite ${LIMITE_PICO_DIR_2003_10} dB(C)): ${resultado.cumpleLimitePico ? 'CUMPLE' : 'NO CUMPLE'}`,
      9, 'bold', 'left', 0, 2
    )
    // Valores de acción: se recalculan con el pico medido sin protector
    // (inputs.LCpico, presente en todos los cálculos impulsivos guardados);
    // el resultado guardado no cambia de forma. Si faltara, se omite.
    if (Number.isFinite(inputs.LCpico)) {
      const accion = textoAccionPico(verificarLimitePico(resultado.LCpicoAtenuado, inputs.LCpico), true)
      if (accion) txt(accion, 9, 'normal', 'left', 0, 2)
    }
    skip(2)
    hline()

    // d. Cita normativa — la misma que en pantalla
    txt(
      `Límite de ${LIMITE_PICO_DIR_2003_10} dB(C) — Directiva 2003/10/CE, Art. 3 (valor límite 140 dB(C); acción superior ` +
      '137 dB(C); acción inferior 135 dB(C)), por remisión del DE 255 Art. 48.',
      8, 'italic', 'left', 0, 4
    )
  }

  const blob = doc.output('blob')
  const filename = `Ruido_${calculo.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${calculo.id}-ruido`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte de Ruido - ${calculo.descripcion}`,
    description: 'Generado automáticamente desde Metodología de Ruido',
    checklist_item_id: 'GT-15',
    uploaded_by: uploadedBy,
  })
}
